/**
 * Per-conversation customer-typing state.
 *
 * Fed by the `conversation.typing` realtime event in App.svelte. Components
 * read `typing.isTyping(conversationId)` reactively to show a "<customer>
 * is typing…" pill. Each entry auto-clears 6 s after the last typing_on
 * frame so a stale signal can't linger if the typing_off frame is dropped.
 *
 * Real-message arrival in App.svelte also calls `clear(conversationId)` so
 * the pill disappears immediately when the customer sends.
 */

const AUTO_CLEAR_MS = 6_000;

class TypingStore {
  private state = $state<Record<string, { at: number }>>({});
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Record a typing event. `on=true` starts/refreshes the indicator with a
   * 6 s auto-clear timer. `on=false` clears immediately.
   */
  set(conversationId: string, on: boolean): void {
    const existing = this.timers.get(conversationId);
    if (existing) {
      clearTimeout(existing);
      this.timers.delete(conversationId);
    }
    if (!on) {
      delete this.state[conversationId];
      return;
    }
    this.state[conversationId] = { at: Date.now() };
    const timer = setTimeout(() => {
      delete this.state[conversationId];
      this.timers.delete(conversationId);
    }, AUTO_CLEAR_MS);
    this.timers.set(conversationId, timer);
  }

  /** Force-clear, e.g. when a real message arrives. */
  clear(conversationId: string): void {
    this.set(conversationId, false);
  }

  /** Reactive read used by Thread.svelte. */
  isTyping(conversationId: string): boolean {
    return Boolean(this.state[conversationId]);
  }
}

export const typing = new TypingStore();
